
import { t } from '@/i18n'
import mqtt from '@/utils/mqtt'
import { getStorageJson } from '@/utils/storage'
import { hasPermission as userHasPermission } from '@/utils/authorization'
import { appendHeartbeatSample } from '@/utils/heartbeatHealth'
import { getEventThemeColor } from '@/utils/theme'
import { getRuntimePlatform } from '@/utils/runtimePlatform'
import { terminalDataSync, terminalRefreshActions } from '@/utils/terminalDataSync'
import { autoResetOrphanMission } from '@/utils/missionSafety'

export default {
  name: 'TerminalRuntime',
  data () {
    return {
      cleanADSBInterval: null,
      dataRefreshTimer: null,
      inventoryRefreshInterval: null,
      dataRefreshRunning: false,
      dataReconcileTick: 0,
      pendingDataRefreshKeys: new Set(),
      pendingDataRefreshShopIds: new Set(),
      mqttRuntimeToken: null,
      mqttLifecycleGeneration: 0,
      mqttSubscriptionClient: null,
      mqttValidationRequest: null,
      mqttSessionInterval: null,
      androidRuntimePaused: false,
      androidSessionChecking: false,
      androidSessionRetryTimer: null,
      terminalDataUnavailable: false
    }
  },
  methods: {
    hasPermission (code) {
      return userHasPermission(this.$store.state.user, code)
    },
    /** 统一刷新终端页面所需的基础数据，供首次进入和手动刷新共同调用。 */
    async refreshTerminalData () {
      const tasks = []
      // 地图终端只读取 NAS 代理公开瓦片地址和实际层级。
      tasks.push(this.$store.dispatch('fetchMapRuntimeConfig'))
      if (this.hasPermission('plane.air.view') || this.hasPermission('terminal.dashboard')) {
        tasks.push(this.$store.dispatch('fetchPlaneClassList'))
        tasks.push(this.$store.dispatch('fetchPlaneModelList'))
        tasks.push(this.$store.dispatch('fetchAirList'))
      }
      if (this.hasPermission('shop.view') || this.hasPermission('terminal.dashboard')) tasks.push(this.$store.dispatch('fetchShopList'))
      if (this.$store.state.user.is_god || this.$store.state.user.is_owner) tasks.push(this.$store.dispatch('fetchAdminList'))
      if (this.hasPermission('plane.site.view') || this.hasPermission('terminal.dashboard')) tasks.push(this.$store.dispatch('fetchSiteList'))
      if (this.hasPermission('plane.route.view') || this.hasPermission('terminal.dashboard')) tasks.push(this.$store.dispatch('fetchRouteList'))
      if (this.hasPermission('product.category.view')) tasks.push(this.$store.dispatch('fetchCategoryList'))
      if (this.hasPermission('broadcast.view')) {
        tasks.push(this.$store.dispatch('fetchBannerList'))
        tasks.push(this.$store.dispatch('fetchNoticeList'))
      }
      if (this.hasPermission('product.spu.view')) tasks.push(this.$store.dispatch('fetchSpuList'))
      if (this.hasPermission('product.sku.view')) tasks.push(this.$store.dispatch('fetchSkuList'))
      if (this.hasPermission('terminal.dashboard')) tasks.push(this.$store.dispatch('fetchPaidOrderList'))
      if (this.hasPermission('message.view')) tasks.push(this.$store.dispatch('fetchMessageList'))
      if (this.$store.state.user.is_god || (this.$store.state.user.permissions || []).length > 0) {
        tasks.push(this.$store.dispatch('fetchMapStyleList', this.$store.state.settings.host))
      }
      // 等待所有请求结束，保留已经成功加载的数据。
      const results = await Promise.allSettled(tasks)
      // 禁限飞区是平台级飞行安全数据，所有已登录终端读取同一份数据。
      results.push(...await Promise.allSettled([this.$store.dispatch('fetchNoflyData')]))
      this.terminalDataUnavailable = results.some(result => result.status === 'rejected')
      if (this.terminalDataUnavailable) {
        throw results.find(result => result.status === 'rejected').reason
      }
    },
    /**
    * @description: 初始化 MQTT 连接和全局主题订阅，只在页面入口创建时执行一次，避免重复连接和重复注册消息回调
    */
    async initMqttSubscribe () {
      const token = this.$store.state.user.token
      const generation = this.mqttLifecycleGeneration
      if (!await this.$store.dispatch('user/fetchMqttCredentials')) return false
      if (!token || token !== this.$store.state.user.token || generation !== this.mqttLifecycleGeneration || this.androidRuntimePaused || this._isDestroyed || this._isBeingDestroyed) return false
      const client = mqtt.mqttConf() // 连接mqtt
      if (!client) return false
      if (this.mqttSubscriptionClient === client) return true
      this.mqttSubscriptionClient = client

      const isGod = Boolean(this.$store.state.user.is_god)
      const shopTopicScope = isGod ? '+' : this.shop_id

      // 普通账号只收本店飞机；god需要接收其可切换商铺中的飞机状态。
      mqtt.doSubscribe(`+/${shopTopicScope}/+`, (mqttRes) => {
        const topic = mqttRes.topic || ''
        const topicParts = topic.split('/')

        if (topicParts.length !== 3) return

        const topicName = topicParts[0]
        const topicShopId = topicParts[1]
        const planeCode = topicParts[2]

        if (!isGod && String(topicShopId) !== String(this.shop_id)) return

        const plane = this.airList.find(item => (
          (String(item.plane_code) === String(planeCode) || String(item.macadd) === String(planeCode)) &&
          (isGod ? String(item.shop_id) === String(topicShopId) : true)
        ))
        if (!plane) return

        if (topicName === 'planeState') {
          this.handlePlaneStateMessage(plane, mqttRes)
        } else if (topicName === 'deviceSwitchState') {
          this.handleDeviceSwitchStateMessage(plane, mqttRes)
        } else if (topicName === 'ADSB') {
          this.handleADSBMessage(mqttRes)
        } else if (topicName === 'eventLog') {
          this.handleEventLogMessage(plane, mqttRes)
        }
      })

      // 订阅游客下单频道
      mqtt.doSubscribe(`refreshQuestList/${shopTopicScope}`, (mqttRes) => {
        const topicParts = String(mqttRes.topic || '').split('/')
        if (topicParts.length !== 2 || topicParts[0] !== 'refreshQuestList') return
        if (!isGod && String(topicParts[1]) !== String(this.shop_id)) return
        this.queueTerminalDataRefresh(['paidOrder', 'sku'], topicParts[1])
      })

      // 普通账号监听本店刷新；god监听全部商铺，确保切换商铺后仍能收到数据更新。
      mqtt.doSubscribe(`terminalDataRefresh/${shopTopicScope}`, (mqttRes) => {
        const topicParts = String(mqttRes.topic || '').split('/')
        if (topicParts.length !== 2 || topicParts[0] !== 'terminalDataRefresh') return
        if (!isGod && String(topicParts[1]) !== String(this.shop_id)) return
        this.handleTerminalDataRefresh(mqttRes)
      })
      // 订阅账号挤下线通知
      mqtt.doSubscribe(`kickLogin/${this.$store.state.user.id}`, (mqttRes) => {
        if (mqttRes.topic !== `kickLogin/${this.$store.state.user.id}`) return

        const newLoginJti = String(mqttRes.msg).trim()
        const userInfo = getStorageJson('userInfo', {})
        const localLoginJti = this.$store.state.user.login_jti || userInfo.login_jti

        if (newLoginJti !== localLoginJti) {
          // 挤下线通知到达后立即断开，不等待路由切换后的组件销毁。
          mqtt.mqttDestroy()
          this.$message.warning(t('账号已在其他地方登录'))
          this.$store
            .dispatch('user/destroyUserAsync')
            .then(() => {
              if (!this.$store.state.user.token) this.$router.replace('/login')
            })
        }
      })

      return true
    },

    handleAndroidPause () {
      if (!getRuntimePlatform().isCordovaAndroid) return

      this.pauseMqttRuntime()
    },

    pauseMqttRuntime () {
      this.mqttLifecycleGeneration += 1
      this.androidRuntimePaused = true
      clearTimeout(this.androidSessionRetryTimer)
      this.androidSessionRetryTimer = null
      mqtt.mqttPause()
    },

    handleAndroidResume () {
      if (!getRuntimePlatform().isCordovaAndroid || document.hidden) return

      this.androidRuntimePaused = false
      this.validateAndroidSessionBeforeMqtt()
    },

    handleMqttPageHide () {
      this.pauseMqttRuntime()
    },

    handleMqttPageShow () {
      if (document.hidden || !this.androidRuntimePaused) return
      this.androidRuntimePaused = false
      this.validateAndroidSessionBeforeMqtt()
    },

    handleMqttVisibility () {
      if (document.hidden) this.pauseMqttRuntime()
      else this.handleMqttPageShow()
    },

    async validateAndroidSessionBeforeMqtt () {
      const token = this.$store.state.user.token
      const generation = this.mqttLifecycleGeneration
      if (!token || this.androidRuntimePaused || this._isBeingDestroyed || this._isDestroyed) return
      if (this.mqttValidationRequest && this.mqttValidationRequest.token === token && this.mqttValidationRequest.generation === generation) return
      const request = { token, generation }
      const current = () => token === this.$store.state.user.token && generation === this.mqttLifecycleGeneration &&
        !this.androidRuntimePaused && !this._isBeingDestroyed && !this._isDestroyed
      this.mqttValidationRequest = request
      this.androidSessionChecking = true
      try {
        // Check the current JTI even when a kick notification was missed while offline.
        const valid = await this.$store.dispatch('user/fetchMqttCredentials', { force: true, quiet: true })
        if (!current()) return
        if (!valid) throw new Error('MQTT session validation failed')
        clearTimeout(this.androidSessionRetryTimer)
        this.androidSessionRetryTimer = null
        if (!mqtt.isConnected()) {
          if (!mqtt.mqttResume()) return
          await this.initMqttSubscribe()
          if (current()) this.refreshPlaneRequest()
        }
      } catch (error) {
        if (current()) {
          // A failed validation must not leave an old socket publishing during retries.
          mqtt.mqttPause()
          clearTimeout(this.androidSessionRetryTimer)
          this.androidSessionRetryTimer = setTimeout(() => {
            this.androidSessionRetryTimer = null
            this.validateAndroidSessionBeforeMqtt()
          }, 3000)
        }
      } finally {
        if (this.mqttValidationRequest === request) {
          this.mqttValidationRequest = null
          this.androidSessionChecking = false
        }
      }
    },

    /**
     * @description: 向当前飞机列表里的所有飞机发送状态刷新请求，用于进入系统或飞机列表更新后主动拉取最新状态
     */
    refreshPlaneRequest () {
      this.airList.forEach(item => {
        if (item.plane_code) mqtt.publishCommand(item, '{"refreshRequest":1}')
      })
    },

    /**
     * @description: 处理后端发来的基础数据刷新通知
     * @payload {"keys":["site","route"]}
     */
    handleTerminalDataRefresh (mqttRes) {
      let data = {}
      try {
        data = JSON.parse(String(mqttRes.msg || '{}'))
      } catch (error) {
        console.error('Error parsing terminalDataRefresh:', error)
        return
      }

      const keys = Array.isArray(data.keys) ? data.keys : [data.key]
      const topicShopId = String(mqttRes.topic || '').split('/')[1] || this.shop_id
      this.queueTerminalDataRefresh(keys, topicShopId)
    },

    /**
     * 合并短时间内来自多个终端的刷新通知，再统一请求，避免同一批更新重复打接口。
     */
    queueTerminalDataRefresh (keys, shopId) {
      if (this._isDestroyed || this._isBeingDestroyed || !this.$store.state.user.token) return
      keys = keys.filter(key => typeof key === 'string' && key.length <= 64).slice(0, 32)
      terminalDataSync.invalidate(keys, this.$store.state.user.token)
      keys.forEach(key => this.pendingDataRefreshKeys.add(key))
      if (shopId) this.pendingDataRefreshShopIds.add(String(shopId))
      if (!this.dataRefreshTimer && !this.dataRefreshRunning) {
        this.dataRefreshTimer = setTimeout(() => this.flushTerminalDataRefresh(), 300)
      }
    },

    async flushTerminalDataRefresh () {
      if (this.dataRefreshRunning) return
      this.dataRefreshTimer = null
      if (this.androidRuntimePaused || this._isDestroyed || this._isBeingDestroyed || !this.$store.state.user.token) return
      this.dataRefreshRunning = true
      const token = this.$store.state.user.token
      const keys = Array.from(this.pendingDataRefreshKeys)
      this.pendingDataRefreshKeys.clear()
      this.pendingDataRefreshShopIds.clear()
      this.dataRefreshTimer = null

      const refreshActionMap = terminalRefreshActions

      const actionSet = new Set()
      keys.forEach(key => {
        const actions = Object.prototype.hasOwnProperty.call(refreshActionMap, key) ? refreshActionMap[key] : null
        if (actions) actions.forEach(action => actionSet.add(action))
      })

      const refreshPermissionMap = {
        fetchShopList: 'shop.view',
        fetchAirList: 'plane.air.view',
        fetchPlaneClassList: 'plane.air.view',
        fetchPlaneModelList: 'plane.air.view',
        fetchSiteList: 'plane.site.view',
        fetchRouteList: 'plane.route.view',
        fetchCategoryList: 'product.category.view',
        fetchBannerList: 'broadcast.view',
        fetchNoticeList: 'broadcast.view',
        fetchSpuList: 'product.spu.view',
        fetchSkuList: 'product.sku.view',
        fetchPaidOrderList: 'terminal.dashboard',
        fetchMessageList: 'message.view'
      }

      const tasks = []
      actionSet.forEach(action => {
        if (!terminalDataSync.needsRefresh(action, token)) return
        if (action === 'fetchAdminList' && !this.$store.state.user.is_god && !this.$store.state.user.is_owner) return
        const permission = refreshPermissionMap[action]
        const canRefreshGroundData = ['fetchShopList', 'fetchAirList', 'fetchPlaneClassList', 'fetchPlaneModelList', 'fetchSiteList', 'fetchRouteList', 'fetchPaidOrderList'].includes(action) && this.hasPermission('terminal.dashboard')
        if (permission && !this.hasPermission(permission) && !canRefreshGroundData) return

        if (action === 'fetchMapStyleList') {
          tasks.push(this.$store.dispatch(action, this.$store.state.settings.host))
        } else {
          tasks.push(this.$store.dispatch(action))
        }
      })

      try {
        const results = await Promise.allSettled(tasks)
        results.filter(result => result.status === 'rejected').forEach(result => console.error('Error refreshing terminal data:', result.reason))
        if (token === this.$store.state.user.token && !this._isDestroyed && typeof this.refreshCurrentPageFromDataUpdate === 'function') {
          await this.refreshCurrentPageFromDataUpdate(keys)
        }
      } catch (error) {
        console.error('Error refreshing terminal data:', error)
      } finally {
        this.dataRefreshRunning = false
        if (this.pendingDataRefreshKeys.size && !this._isDestroyed && !this._isBeingDestroyed) {
          this.dataRefreshTimer = setTimeout(() => this.flushTerminalDataRefresh(), 300)
        }
      }
    },

    /** 飞控心跳离线后保留最后遥测快照，只清理不能继续生效的实时控制状态。 */
    resetOfflinePlaneState (plane) {
      if (!plane || !plane.planeState) return

      Object.assign(plane.planeState, {
        online: false,
        missionReady: false,
        orphanResetPhase: 'waiting',
        flightOfflineSince: plane.planeState.flightOfflineSince || Date.now(),
        // 离线后无法确认真实锁定状态，不能用 false 误导为“已锁定”。
        isUnlock: null,
        heartBeat: null,
        heartbeatSamples: [],
        heartbeatObservedAt: 0,
        heartbeatHealthUpdatedAt: 0,
        isDescentActive: false,
        oscStatus: false,
        oscStatusTick: 0
      })
    },

    /**
     * @description: 处理飞机状态主题
     * @topic planeState/{shop_id}/{plane_code}
     * @payload 飞机端定时上报的状态集合，例如心跳、电池、位置、模式、重量、钩子状态等
     * @param {Object} plane 当前 MQTT 主题 plane_code 对应的飞机对象
     * @param {Object} mqttRes MQTT 消息对象，包含 topic 和 msg
     */
    handlePlaneStateMessage (plane, mqttRes) {
      try {
        const jsonData = JSON.parse(mqttRes.msg.trim())
        const wasOnline = plane.planeState.online

        // 任意有效 planeState 消息都表示 ESP32 通信正常，与飞控心跳状态分开判断
        plane.planeState.lastTelemetryAt = Date.now()
        plane.planeState.espOnline = true
        plane.planeState.espLastSeenAt = Date.now()
        plane.planeState.espRandom = Math.random()
        if (plane.planeState.espOnlineTimeout) {
          clearTimeout(plane.planeState.espOnlineTimeout)
        }
        plane.planeState.espOnlineTimeout = setTimeout(() => {
          plane.planeState.espOnline = false
        }, 10000)

        for (const key in jsonData) {
          if (key === 'heartBeat') {
            // 心跳每次更新一个随机数，方便其他组件 watch 到“心跳刷新”事件
            const heartBeat = Number(jsonData.heartBeat)
            const hasFlightHeartbeat = Number.isFinite(heartBeat) && heartBeat > 0

            if (hasFlightHeartbeat) {
              appendHeartbeatSample(plane.planeState)
              plane.planeState.heartRandom = Math.random()
              plane.planeState.heartBeat = heartBeat
              plane.planeState.isUnlock = (heartBeat & 128) !== 0
              plane.planeState.lastFlightHeartbeatAt = Date.now()
              plane.planeState.flightOfflineSince = 0

              // 收到有效飞控心跳即认为飞机在线；如果 10 秒内没有新心跳，则标记离线
              plane.planeState.online = true
              if (plane.planeState.onlineTimeout) {
                clearTimeout(plane.planeState.onlineTimeout)
              }
              plane.planeState.onlineTimeout = setTimeout(() => {
                this.resetOfflinePlaneState(plane)
              }, 10000)
            } else {
              // ESP32 未连接飞控时仍会上报空字符串或 0，不能据此点亮绿色心形
              if (plane.planeState.onlineTimeout) {
                clearTimeout(plane.planeState.onlineTimeout)
                plane.planeState.onlineTimeout = null
              }
              this.resetOfflinePlaneState(plane)
            }
          } else if (key === 'position') {
            // position 是 JSON 字符串，转换后再写入轨迹数组
            const position = JSON.parse(jsonData.position)

            const lon = Number(position.lon) / 10e6
            const lat = Number(position.lat) / 10e6
            const alt = Number(position.alt)

            // 经纬度无效或为 0 时不写入轨迹，避免地图路线被拉到 (0, 0)
            if (Number.isFinite(lon) && Number.isFinite(lat) && lon !== 0 && lat !== 0) {
              plane.planeState.position.push([lon, lat, alt])

              // 限制轨迹缓存长度，避免长时间运行导致数组过大
              const trackPointLimit = this.$store.getters['app/getOverviewTrackPointLimit']
              if (plane.planeState.position.length > trackPointLimit) {
                plane.planeState.position.splice(0, plane.planeState.position.length - trackPointLimit)
              }
            }
          } else if (key === 'homePosition') {
            // 返航点也是 JSON 字符串，转换经纬度单位后保存
            const homePosition = JSON.parse(jsonData.homePosition)
            homePosition.lon = homePosition.lon / 10e6
            homePosition.lat = homePosition.lat / 10e6
            homePosition.alt = Number(homePosition.alt)
            plane.planeState[key] = homePosition
          } else if (key === 'buzzerEnable') {
            this.$store.commit('setPlaneDeviceSwitchState', {
              planeId: plane.id,
              deviceSwitchState: { buzzerEnable: jsonData[key] }
            })
          } else {
            // 其他普通状态字段直接写入 planeState
            plane.planeState[key] = jsonData[key]

            // 飞控摆动状态每次上报都记录刷新时间，关闭遮罩后如果仍收到 1 会重新弹出
            if (key === 'oscStatus') {
              this.$set(plane.planeState, 'oscStatusTick', Date.now())
            }
          }
        }
        if (!wasOnline && plane.planeState.online) this.queueTerminalDataRefresh(['paidOrder', 'site', 'route'], plane.shop_id)
        if (this.hasPermission('terminal.dashboard') && Object.prototype.hasOwnProperty.call(jsonData, 'heartBeat')) {
          autoResetOrphanMission(plane, this.$store)
        }
      } catch (error) {
        console.error('Error parsing planeState:', error)
      }
    },

    /**
     * @description: 处理飞机事件日志主题
     * @topic eventLog/{shop_id}/{plane_code}
     * @payload 飞机端 addLogMessage() 输出的事件日志文本，例如 ESP@ Hook_stop / FC@ PreArm...
     * @param {Object} plane 当前 MQTT 主题 plane_code 对应的飞机对象
     * @param {Object} mqttRes MQTT 消息对象，包含 topic 和 msg
     */
    getEventLogColor (sourceType) {
      return getEventThemeColor(sourceType)
    },
    parseEventLogMessage (message) {
      const rawMessage = String(message || '').trim()
      if (!rawMessage) return null

      let payload = null
      try {
        payload = JSON.parse(rawMessage)
      } catch (error) {
        payload = null
      }

      if (payload && typeof payload === 'object') {
        return {
          sourceType: payload.source_type || payload.sourceType || 'esp',
          content: payload.content || payload.message || payload.msg || ''
        }
      }

      if (rawMessage.startsWith('FC@')) {
        return {
          sourceType: 'fc',
          content: rawMessage.slice(3).trim()
        }
      }

      if (rawMessage.startsWith('ESP@')) {
        return {
          sourceType: 'esp',
          content: rawMessage.slice(4).trim()
        }
      }

      return {
        sourceType: 'esp',
        content: rawMessage
      }
    },
    handleEventLogMessage (plane, mqttRes) {
      const eventLog = this.parseEventLogMessage(mqttRes.msg)
      if (!eventLog || !eventLog.content) return

      if (eventLog.content === 'Mqtt_HOOK_FAULT_MANUAL_REJECT_NOT_DISARMED') {
        this.$message.warning(t('绕线故障后仅允许飞控加锁状态下用 Pad 收钩或放钩'))
      }

      this.$store.dispatch('fetchLog', {
        planeId: plane.id,
        planeName: plane.name || plane.plane_code || '未知飞机',
        sourceType: eventLog.sourceType,
        content: eventLog.content,
        color: this.getEventLogColor(eventLog.sourceType)
      })
    },
    /**
     * @description: 处理设备开关状态集合主题
     * @topic deviceSwitchState/{shop_id}/{plane_code}
     * @payload 设备开关状态集合，例如 {"buzzerEnable": false}
     * @param {Object} plane 当前 MQTT 主题 plane_code 对应的飞机对象
     * @param {Object} mqttRes MQTT 消息对象，包含 topic 和 msg
     */
    handleDeviceSwitchStateMessage (plane, mqttRes) {
      try {
        const deviceSwitchState = JSON.parse(mqttRes.msg.trim())

        // 通过 Vuex mutation 更新指定飞机的设备开关集合，保证 Vue 响应式
        this.$store.commit('setPlaneDeviceSwitchState', {
          planeId: plane.id,
          deviceSwitchState
        })
      } catch (error) {
        console.error('Error parsing deviceSwitchState:', error)
      }
    },

    /**
     * @description: 处理 ADSB 数据主题
     * @topic ADSB/{shop_id}/{plane_code}
     * @payload 飞机端从飞控解析出的 ADSB 目标数据，例如 icao、lat、lon、alt、heading
     * @param {Object} mqttRes MQTT 消息对象，包含 topic 和 msg
     */
    handleADSBMessage (mqttRes) {
      try {
        const data = JSON.parse(mqttRes.msg)

        // ADSBList 是全局活跃 ADSB 目标列表，按 icao 更新或插入
        this.$store.commit('setADSBList', data)
      } catch (error) {
        console.error('Error parsing ADSB:', error)
      }
    }
  },
  computed: {
    shop_id () {
      return this.$store.state.user.shop_id
    },
    airList () {
      return this.$store.state.airList
    },
    shopList () {
      return this.$store.state.shopList
    }
  },
  mounted () {
    document.addEventListener('pause', this.handleAndroidPause, false)
    document.addEventListener('resume', this.handleAndroidResume, false)
    document.addEventListener('visibilitychange', this.handleMqttVisibility, false)
    window.addEventListener('pagehide', this.handleMqttPageHide)
    window.addEventListener('pageshow', this.handleMqttPageShow)
    this.mqttSessionInterval = setInterval(() => {
      if (!this.androidRuntimePaused) this.validateAndroidSessionBeforeMqtt()
    }, 60000)
    if (document.hidden) this.handleMqttVisibility()

    // Notifications are the fast path; reconcile periodically in case a message was lost.
    this.inventoryRefreshInterval = setInterval(() => {
      if (this.androidRuntimePaused || document.hidden || !this.$store.state.user.token) return
      this.dataReconcileTick += 1
      const keys = this.hasPermission('product.sku.view') ? ['sku'] : []
      if (this.hasPermission('terminal.dashboard')) keys.push('paidOrder', 'site')
      if (this.hasPermission('plane.site.view') || this.hasPermission('terminal.dashboard')) keys.push('site')
      if (this.hasPermission('plane.air.view') || this.hasPermission('terminal.dashboard')) keys.push('air')
      const user = this.$store.state.user
      if (this.dataReconcileTick % 4 === 0 && (user.is_god || user.is_owner || (user.permissions || []).length)) {
        keys.push(...Object.keys(terminalRefreshActions))
      }
      if (keys.length) this.queueTerminalDataRefresh(keys, this.shop_id)
    }, 30000)

    // 定时清理过期的ADSB数据
    this.cleanADSBInterval = setInterval(() => {
      if (this.$store.state.ADSBList.length > 0) {
        this.$store.commit('cleanExpiredADSBList')
      }
    }, 5000)
  },
  created () {
    /* init */
    const token = this.$store.state.user.token
    this.mqttRuntimeToken = token
    this.refreshTerminalData().catch(error => {
      console.error('Error loading terminal data:', error)
    }).then(async () => {
      if (!token || token !== this.$store.state.user.token) return
      const canInitialize = this.$store.state.user.is_god || (
        this.shop_id && (this.$store.state.user.permissions || []).length > 0
      )
      if (canInitialize && !this._isBeingDestroyed && !this._isDestroyed) {
        await this.initMqttSubscribe()
        this.refreshPlaneRequest()
      }
    }).catch(error => {
      console.error('Error loading terminal data:', error)
    })
  },
  watch: {
    '$store.state.app.mqttState' (connected) {
      if (connected) this.queueTerminalDataRefresh(Object.keys(terminalRefreshActions), this.shop_id)
    },
    airList (res) {
      if (!res || res.length === 0) return
      this.refreshPlaneRequest()
    }
  },
  destroyed () {
    document.removeEventListener('pause', this.handleAndroidPause, false)
    document.removeEventListener('resume', this.handleAndroidResume, false)
    document.removeEventListener('visibilitychange', this.handleMqttVisibility, false)
    window.removeEventListener('pagehide', this.handleMqttPageHide)
    window.removeEventListener('pageshow', this.handleMqttPageShow)
    this.mqttLifecycleGeneration += 1
    clearInterval(this.mqttSessionInterval)
    clearInterval(this.cleanADSBInterval)
    clearInterval(this.inventoryRefreshInterval)
    clearTimeout(this.dataRefreshTimer)
    this.dataRefreshTimer = null
    clearTimeout(this.androidSessionRetryTimer)
    this.pendingDataRefreshKeys.clear()
    this.pendingDataRefreshShopIds.clear()
    mqtt.mqttDestroy(this.mqttRuntimeToken)// 不清除其他账号刚建立的新会话
  }
}
